/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import { STIcon } from 'components/Brand/Icons/STIcon';
import Popover from 'components/Core/Popover';
import Tag from 'components/Core/Tag';
import { MenuItem } from '@mui/material';
import { Col, Row } from 'components/Core/Grid';
import Pagination from 'components/Core/Pagination';
import Button from 'components/Core/Button';
import Dropdown from 'components/Core/Dropdown';
import HyperLink from 'components/Core/HyperLink';
import { SearchInput } from 'components/Core/TextField/SearchInput';
import { SectionLayout } from 'components/Sections/SectionLayout';
import useBreakpointView from 'utils/useBreakpointView';
import './SearchCardSection.less';
import CardsGroup from 'components/Core/Cards/CardsGroup';
import Marquee from 'components/Core/Marquee';
import SweetScrollContext from 'utils/SweetScrollContext';

const sortData = (data, sort = '') => {
  data.sort((a, b) => {
    const dateDiff = new Date(a.publishDateRaw) - new Date(b.publishDateRaw);
    return sort === 'Oldest' ? dateDiff : -dateDiff;
  });
  return data;
};

const ContentSearchSection = ({
  allContent = [],
  hubType,
  industries = [],
  topics = [],
  contentTypes = [],
  pageSize = 10,
  tagsCookie,
  columns = 2,
  ItemCard,
  fixedTags = [],
  selectedTag,
  showTags = true,
  location,
  page = 1,
}) => {
  const { scrollToAnchor } = useContext(SweetScrollContext);
  const [allPosts] = useState([...sortData(allContent)]);
  const [filteredPosts, setFilteredPosts] = useState([
    ...allContent.slice((page - 1) * pageSize, page * pageSize),
  ]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tempTags, setTempTags] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    currentPage: page,
    numPages: Math.ceil(allPosts.length / pageSize),
  });
  const defaultSort = 'Most Recent';
  const [selectedSort, setSelectedSort] = useState(defaultSort);
  const [searchTerm, setSearchTerm] = useState();
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortMenuWidth, setSortMenuWidth] = useState(0);
  const isMobile = useBreakpointView(['xs', 'sm']);
  const showMobileTagList = useBreakpointView(['xs', 'sm', 'md']);
  const isWideScreen = useBreakpointView(['xl', 'xxl']);
  const [mobilePopupVisible, setMobilePopupVisible] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const topicsPriority = ['Product Announcements', 'Residential', 'Commercial'];
  topics.sort((a, b) => topicsPriority.indexOf(b.name) - topicsPriority.indexOf(a.name));

  const filterSortSearch = ({
    term = searchTerm,
    sort = selectedSort,
    tags = selectedTags,
    page = 1,
  }) => {
    let fPosts =
      tags.length > 0 &&
      tags.some((x) => industries.some((y) => y.name === x) || topics.some((y) => y.name === x))
        ? allPosts.filter(
            (post) =>
              (industries?.length &&
                post?.seo?.taxonomy?.industries?.some(
                  (x) => tags.includes(x) && industries.some((y) => y.name === x),
                )) ||
              (topics?.length &&
                post?.seo?.taxonomy?.topics?.some(
                  (x) => tags.includes(x) && topics.some((y) => y.name === x),
                )) ||
              (topics?.length &&
                post?.seo?.taxonomy?.businessFocus?.some(
                  (x) => tags.includes(x) && topics.some((y) => y.name === x),
                )),
          )
        : allPosts;

    if (contentTypes?.length && contentTypes.some((x) => tags.includes(x.name))) {
      fPosts = fPosts.filter((post) =>
        tags.some((x) =>
          contentTypes.some((y) => x === y.name && (y?.value || y?.name) === post?.contentType),
        ),
      );
    }

    if (term) {
      fPosts = fPosts.filter(
        (post) =>
          post.title?.toLowerCase().includes(term?.toLowerCase()) ||
          post?.excerpt?.excerpt?.toLowerCase().includes(term?.toLowerCase()),
      );
    }
    // Exclude if the title starts with [Test] or pageUrl contains 'test'
    // Except for pages containing 'testimonial'
    fPosts = fPosts?.filter((post) => {
      const titleStartsWithTest = /^\[test\]/i.test(post.title);
      const pageUrlHasTest = post.seo?.pageUrl?.includes('test');
      const isTestimonialPage = post.seo?.pageUrl?.includes('testimonial');
      return !(titleStartsWithTest || (pageUrlHasTest && !isTestimonialPage));
    });

    sortData(fPosts, sort);

    setFilteredPosts(fPosts.slice((page - 1) * pageSize, page * pageSize));
    setPageInfo({
      currentPage: fPosts.length > 0 ? page : 0,
      numPages: Math.ceil(fPosts.length / pageSize),
    });
  };

  useEffect(() => {
    const cookiesTags = tagsCookie && Cookies.get(tagsCookie);
    const newTags = [];
    setSearchTerm('');
    setSelectedSort(defaultSort);

    if (cookiesTags?.length > 0) {
      const tags = JSON.parse(cookiesTags);
      newTags.push(...tags);
    }

    if (selectedTag) {
      const tags = [...topics, ...industries]
        .filter((x) => x.name.toLowerCase() === selectedTag.toLowerCase())
        .map((x) => x.name);
      newTags.length = 0;
      newTags.push(...tags);
    }

    const queryParams = window?.location?.search ? queryString.parse(window?.location?.search) : {};
    if (queryParams.topic) {
      newTags.length = 0;
      newTags.push(queryParams.topic);
    }

    if (newTags.length > 0) {
      setSelectedTags(newTags);
      filterSortSearch({ sort: defaultSort, tags: newTags, term: '' });
    }
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobilePopupVisible ? 'hidden' : 'auto';
  }, [mobilePopupVisible]);

  useEffect(() => {
    if (!isMobile) document.body.style.overflow = 'auto';
  }, [isMobile]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterSortSearch({ term });
  };

  const showSearch = () => {
    setMobileSearchVisible(true);

    document.addEventListener('mouseup', (event) => {
      const container = document.getElementsByClassName('search-menu-popup')[0];
      if (container && !container.contains(event.target)) {
        setMobileSearchVisible(false);
      }
    });
  };

  const sortClick = (sort) => {
    setSelectedSort(sort);
    filterSortSearch({ sort });
    setSortAnchorEl(null);
  };

  const handleSortClick = (e) => {
    setSortAnchorEl(e?.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  useEffect(() => {
    if (sortAnchorEl) {
      setSortMenuWidth(sortAnchorEl.clientWidth);
    }
  }, [sortAnchorEl]);

  const setTagsCookie = (tags) => {
    if (!tagsCookie) {
      return;
    }
    const tagsString = JSON.stringify(tags);
    Cookies.set(tagsCookie, tagsString);
  };

  const filterClick = () => {
    setSelectedTags([...tempTags]);
    filterSortSearch({ sort: selectedSort, tags: tempTags });
    setTagsCookie([...tempTags]);
    setMobilePopupVisible(false);
  };

  const clearClick = () => {
    setSelectedSort(defaultSort);
    setSelectedTags([]);
    setTempTags([]);
    setSearchTerm('');
    filterSortSearch({ term: '', sort: defaultSort, tags: [] });
    setTagsCookie([]);
  };

  const toggleTag = (tags, tag) => {
    let updatedTags = [...tags];
    if (!updatedTags.includes(tag)) {
      updatedTags = [...updatedTags, tag];
    } else {
      updatedTags = [...updatedTags.filter((t) => t !== tag)];
    }
    return updatedTags;
  };

  const tagClick = (tag) => {
    if (isMobile) {
      setTempTags((prevTags) => toggleTag(prevTags, tag));
    } else {
      setSelectedTags((prevTags) => {
        const updatedTags = toggleTag(prevTags, tag);
        filterSortSearch({ sort: selectedSort, tags: updatedTags });
        setTagsCookie(updatedTags);
        return updatedTags;
      });
    }
  };

  const checkTagSelected = (tag) => (isMobile ? tempTags : selectedTags).includes(tag);

  const FilterMenu = (
    <div className="filter-menu-popup" data-testid="filter-menu">
      <h6>Filter</h6>
      {topics?.length > 0 && (
        <>
          <p>Topics</p>
          <div className="filter-menu--tags-container">
            {topics
              .map((x) => x.name)
              .filter((topic) => topic !== 'All Topics')
              .map((topic, index) => (
                <Tag
                  className={checkTagSelected(topic) ? 'selected' : ''}
                  onClick={() => tagClick(topic)}
                  key={`topic-${index}`}
                >
                  {topic}
                </Tag>
              ))}
          </div>
        </>
      )}
      {industries?.length > 0 && (
        <>
          <p>Industries</p>
          <div className="filter-menu--tags-container">
            {industries
              .map((x) => x.name)
              .filter((topic) => topic !== 'All Industries')
              .map((industry, index) => (
                <Tag
                  className={checkTagSelected(industry) ? 'selected' : ''}
                  onClick={() => tagClick(industry)}
                  key={`industries-${index}`}
                >
                  {industry}
                </Tag>
              ))}
          </div>
        </>
      )}
      {contentTypes?.length > 0 && (
        <>
          <p>Content Type</p>
          <div className="filter-menu--tags-container">
            {contentTypes.map(({ name }, index) => (
              <Tag
                className={checkTagSelected(name) ? 'selected' : ''}
                onClick={() => tagClick(name)}
                key={`type-${index}`}
              >
                {name}
              </Tag>
            ))}
          </div>
        </>
      )}
      <Row justify="end" className="filter-footer">
        <Col>
          <Button type="white" size="small" shadow={false} onClick={clearClick}>
            Clear
          </Button>
          <Button
            className="max-md"
            type="secondary"
            size="small"
            shadow={false}
            onClick={filterClick}
          >
            Confirm
          </Button>
        </Col>
      </Row>
    </div>
  );

  const SearchMenu = () => (
    <div className="search-menu-popup">
      <SearchInput handleSearch={handleSearch} allowClear value={searchTerm} autoFocus />
    </div>
  );

  const visibleTopics = [
    ...fixedTags,
    ...topics
      .filter((topic) => topic.name !== 'All Topics' && !fixedTags.some((y) => y === topic.name))
      .sort((a, b) => b.count - a.count)
      .map((x) => x.name)
      .slice(0, isWideScreen ? 4 : 2),
  ];

  const TagList = () => (
    <div>
      {selectedTags.map((tag, index) => (
        <h6 key={`tag-list-${index}`}>{tag}</h6>
      ))}
    </div>
  );

  const FilterList = useCallback(() => {
    const scrollThreshold = 50;
    const tagSize = selectedTags.map((x) => x.length).reduce((accum, a) => accum + a, 0);
    return (
      <TagRow>
        <div>
          <h6>Results for: </h6>
        </div>
        <TagContainer fade={tagSize > 50}>
          {tagSize > scrollThreshold && (
            <Marquee speed={30} style={{ overflow: 'hidden' }}>
              <TagList />
            </Marquee>
          )}
          {tagSize <= scrollThreshold && <TagList />}
        </TagContainer>
        <div>
          <Button type="white" size="small" shadow={false} onClick={clearClick}>
            Clear Search
          </Button>
        </div>
      </TagRow>
    );
  }, [selectedTags]);

  const MobileFilterList = () => {
    return (
      <MobileTagRow>
        <span>Results for:</span>
        {selectedTags.map((tag) => (
          <span>{tag}</span>
        ))}
      </MobileTagRow>
    );
  };

  return (
    <StyledSectionLayout className="content-search-section--main-container" backgroundColor="White">
      <MenuArea data-testid="menu-area">
        <Row gutter={0} align="bottom">
          <Col lg={19} md={19} sm={10} xs={10} className="section-filter">
            <Popover
              content={<SearchMenu />}
              placement="bottom-start"
              disablePortal
              className="search-menu-tooltip"
              {...(isMobile ? { visible: false } : {})}
            >
              <div
                onClick={showSearch}
                data-testid="search-icon-button"
                className={`search-icon-container${searchTerm ? ' selected' : ''}`}
              >
                <STIcon type="icon-search" height="30" />
              </div>
            </Popover>
            <Popover
              content={FilterMenu}
              placement="bottom-start"
              className="mobile-overlay filter-tooltip"
              {...(isMobile ? { visible: mobilePopupVisible } : {})}
            >
              <div
                onClick={() => {
                  if (isMobile) {
                    setTempTags([...selectedTags]);
                    setMobilePopupVisible(true);
                  }
                }}
                data-testid="filter-icon-button"
                className={`filter-icon-container${selectedTags.length > 0 ? ' selected' : ''}`}
              >
                <STIcon type="icon-filter" />
              </div>
            </Popover>
            <Row className="filter-tag-container min-lg" align="middle" gutter="20">
              <Col>
                {showTags &&
                  visibleTopics.map((topic) => (
                    <Tag
                      className={checkTagSelected(topic) ? 'selected' : ''}
                      onClick={() => tagClick(topic)}
                    >
                      {topic}
                    </Tag>
                  ))}
                {!showTags && selectedTags.length > 0 && <FilterList />}
              </Col>
            </Row>
          </Col>
          <Col lg={5} md={5} sm={14} xs={14} className="section-sort">
            <fieldset>
              <legend>Sort</legend>
              <button data-testid="sort-button" onClick={handleSortClick}>
                <span>{selectedSort}</span>
                <STIcon className="icon-down-caret" type="icon-down-caret" width="10px" />
              </button>
              <Dropdown
                className="filter-sort-menu"
                anchorEl={sortAnchorEl}
                open={!!sortAnchorEl}
                variant="selectedMenu"
                onClose={handleSortClose}
                placement="bottom"
                PaperProps={{ style: { width: sortMenuWidth } }}
              >
                {[defaultSort, 'Oldest'].map((option, index) => (
                  <MenuItem
                    onClick={() => sortClick(option)}
                    selected={option === selectedSort}
                    key={`menu-item-${index}`}
                  >
                    <span>
                      {option}
                      {option === selectedSort ? (
                        <STIcon className="icon-check" type="icon-check" />
                      ) : (
                        ''
                      )}
                    </span>
                  </MenuItem>
                ))}
              </Dropdown>
            </fieldset>
          </Col>
        </Row>
      </MenuArea>
      {mobileSearchVisible && isMobile && <SearchMenu className="max-md" />}
      {showMobileTagList && !showTags && selectedTags.length > 0 && <MobileFilterList />}
      <CardSection>
        {filteredPosts?.length > 0 && (
          <CardsGroup cols={columns}>
            {filteredPosts.map((data, index) => ItemCard(data, index))}
          </CardsGroup>
        )}
        {filteredPosts && filteredPosts.length === 0 && (
          <NoResults>
            <p>
              No results found, <HyperLink onClick={clearClick}>Clear Filters</HyperLink>
            </p>
          </NoResults>
        )}
      </CardSection>
      {pageInfo.currentPage > 0 && (
        <Pagination
          template={hubType}
          pageContext={pageInfo}
          onPage={(i, e) => {
            if (e) {
              e.preventDefault();
            }
            if (hubType) {
              window.history.pushState({}, '', `/${hubType}/page/${i}`);
            }
            filterSortSearch({ page: i });
            scrollToAnchor('#card-group');
            return false;
          }}
        />
      )}
    </StyledSectionLayout>
  );
};

const StyledSectionLayout = styled(SectionLayout)`
  & > div {
    padding: 0;
  }
`;

const MobileTagRow = styled.div`
  width: 100%;
  margin: 20px 0 20px 0;
  clear: both;
  span {
    float: left;
    font-weight: 500;
    font-size: var(--font-size-13);
    font-weight: normal;
    white-space: nowrap;
    margin-right: 5px;
    &:first-of-type {
      margin-right: 5px;
    }
    &:nth-of-type(n + 2) {
      font-weight: 500;
      &::after {
        content: ',';
      }
    }
    &:last-of-type::after {
      content: '';
    }
  }
  &:after {
    content: '';
    display: block;
    clear: both;
  }
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  > div {
    display: flex;
    align-items: center;
    height: 100%;
    > h6 {
      margin: 0;
      font-weight: normal;
    }
  }
  button {
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
    border: 1px solid var(--grey-3) !important;
    padding: 10px 15px;
    &:hover {
      background-color: white;
    }
  }
`;

const TagContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 450px;
  @media (max-width: 1199px) {
    max-width: 420px;
  }
  @media (max-width: 992px) {
    max-width: 390px;
  }
  height: 100%;
  white-space: nowrap;
  overflow: hidden;
  margin-left: 8px;
  margin-right: 15px;
  cursor: ${(props) => (!props.fade ? 'pointer' : 'arrow')};
  &::before {
    content: ' ';
    width: ${(props) => (!props.fade ? '0' : '50px')};
    background: linear-gradient(to right, #ffffff00, #ffffffff);
    position: absolute;
    height: 100%;
    right: 134px;
    z-index: 2;
  }
  div {
    display: flex;
    h6 {
    margin-right: 8px;
    margin-bottom: 0px;
    &::after {
      content: ',';
    }
    &:last-of-type::after {
      content: '';
    }
  }
  .initial-child-container {
    width: 100%;
    .child {
      display: flex !important;
      width: 100% !important;
    }
  }
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  background: var(--grey-1);
  color var(--darkest);
  margin: 40px 0;
`;

const CardSection = styled.div`
  .section-blog-card--card-container {
    margin-top: 60px;
    margin-bottom: 60px;
  }

  .blog-posts--show-more-btn-wrapper {
    text-align: center;
  }
`;

const MenuArea = styled.div`
  .MuiGrid-item {
    display: flex;
    justify-content: start;
    @media (max-width: 575px) {
      justify-content: center;
    }
  }
  .section-sort {
    justify-content: flex-end !important;
  }
  .section-filter {
    align-items: flex-start;
  }
`;

export default ContentSearchSection;
